import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BANDS = [
  { label: "760+", adj: -0.25, pmi: 0.24 },
  { label: "740–759", adj: -0.125, pmi: 0.32 },
  { label: "720–739", adj: 0, pmi: 0.41 },
  { label: "700–719", adj: 0.15, pmi: 0.54 },
  { label: "680–699", adj: 0.3, pmi: 0.7 },
  { label: "660–679", adj: 0.5, pmi: 0.88 },
  { label: "640–659", adj: 0.75, pmi: 1.11 },
  { label: "620–639", adj: 1.05, pmi: 1.39 },
];

const COUNTIES = [
  { name: "Kaufman", rate: 2.3 },
  { name: "Dallas", rate: 2.22 },
  { name: "Tarrant", rate: 2.1 },
  { name: "Collin", rate: 1.96 },
  { name: "Travis", rate: 1.81 },
];

const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

const useCountUp = (value) => {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(value);
      prev.current = value;
      return;
    }
    const from = prev.current;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / 300, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (value - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else prev.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return display;
};

function compute(price, band, county) {
  const rate = 6.6 + band.adj;
  const loan = price * 0.9;
  const r = rate / 100;
  const pi = (loan * (r / 12)) / (1 - Math.pow(1 + r / 12, -360));
  const pmi = (loan * band.pmi) / 100 / 12;
  const taxes =
    (price * county.rate) / 100 / 12 -
    (100000 * (county.rate * 0.52)) / 100 / 12;
  const insurance = (4400 * Math.pow(price / 350000, 0.8)) / 12;
  return { rate, pi, pmi, taxes, insurance, total: pi + pmi + taxes + insurance };
}

const SEGMENTS = [
  { key: "pi", label: "P&I", color: "#0C1F2C" },
  { key: "pmi", label: "PMI", color: "#B7791F" },
  { key: "taxes", label: "Taxes", color: "#3E6B8C" },
  { key: "insurance", label: "Insurance", color: "#6C8C3E" },
];

export const HeroCalculator = () => {
  const [price, setPrice] = useState(375000);
  const [bandIdx, setBandIdx] = useState(2);
  const [countyIdx, setCountyIdx] = useState(4);

  const out = useMemo(
    () => compute(price, BANDS[bandIdx], COUNTIES[countyIdx]),
    [price, bandIdx, countyIdx],
  );
  const best = useMemo(
    () => compute(price, BANDS[0], COUNTIES[countyIdx]),
    [price, countyIdx],
  );
  const delta = out.total - best.total;
  const animatedTotal = useCountUp(out.total);

  return (
    <motion.div
      initial={{ opacity: 0, y: 32, rotate: 1.2 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
      data-testid="hero-calculator-card"
      className="relative rounded-2xl bg-white p-6 shadow-[0_24px_80px_rgba(0,0,0,0.35)] sm:p-7"
    >
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[11px] tracking-[0.2em] text-muted-ink">
          YOUR REAL PAYMENT
        </p>
        <p className="font-mono text-[11px] text-muted-ink">LIVE</p>
      </div>

      <p
        data-testid="calc-total"
        className="mt-2 font-mono text-4xl font-semibold text-ink sm:text-[2.75rem] sm:leading-tight"
      >
        {fmt(animatedTotal)}
        <span className="text-xl text-muted-ink">/mo</span>
      </p>

      <div
        data-testid="calc-stacked-bar"
        className="mt-4 flex h-3 w-full overflow-hidden rounded-full"
      >
        {SEGMENTS.map((s) => (
          <div
            key={s.key}
            style={{
              width: `${(out[s.key] / out.total) * 100}%`,
              backgroundColor: s.color,
            }}
            className="transition-all duration-300"
          />
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
        {SEGMENTS.map((s) => (
          <p key={s.key} className="flex items-center gap-1.5 font-mono text-[11px] text-muted-ink">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ backgroundColor: s.color }}
            />
            {s.label} <span className="font-medium text-ink">{fmt(out[s.key])}</span>
          </p>
        ))}
      </div>

      <p data-testid="calc-delta-line" className="mt-4 border-l-2 border-gold pl-3 font-body text-[13px] leading-relaxed text-muted-ink">
        at{" "}
        <span className="font-mono font-semibold text-ink">
          {out.rate.toFixed(2)}%
        </span>{" "}
        for your credit band — a 760+ buyer in the same house pays{" "}
        <span className="font-mono font-semibold text-lens">
          {fmt(Math.abs(delta))}
        </span>{" "}
        less per month.
      </p>

      <div className="mt-6 space-y-5">
        <div>
          <div className="flex items-baseline justify-between">
            <label className="font-body text-sm font-medium text-ink">
              Home price
            </label>
            <span data-testid="calc-price-value" className="font-mono text-sm font-semibold text-ink">
              {fmt(price)}
            </span>
          </div>
          <Slider
            data-testid="calc-price-slider"
            aria-label="Home price"
            value={[price]}
            onValueChange={(v) => setPrice(v[0])}
            min={150000}
            max={800000}
            step={5000}
            className="mt-3 [&_[role=slider]]:border-lens [&_[role=slider]]:bg-white [&_.bg-primary]:bg-lens"
          />
          <div className="mt-1.5 flex justify-between font-mono text-[10px] text-muted-ink">
            <span>$150k</span>
            <span>$800k</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="font-body text-sm font-medium text-ink">
              Credit score
            </label>
            <Select
              value={String(bandIdx)}
              onValueChange={(v) => setBandIdx(Number(v))}
            >
              <SelectTrigger data-testid="calc-credit-select" aria-label="Credit score band" className="mt-2 h-10 border-hairline bg-white font-mono text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BANDS.map((b, i) => (
                  <SelectItem key={b.label} value={String(i)} className="font-mono text-sm">
                    {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="font-body text-sm font-medium text-ink">
              County
            </label>
            <Select
              value={String(countyIdx)}
              onValueChange={(v) => setCountyIdx(Number(v))}
            >
              <SelectTrigger data-testid="calc-county-select" aria-label="County" className="mt-2 h-10 border-hairline bg-white font-body text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTIES.map((c, i) => (
                  <SelectItem key={c.name} value={String(i)} className="text-sm">
                    {c.name}{" "}
                    <span className="font-mono text-muted-ink">({c.rate.toFixed(2)}%)</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <p className="mt-5 border-t border-hairline pt-4 font-body text-[11px] text-muted-ink">
        Estimate. 10% down, 30-yr fixed. Homestead applied.
      </p>
    </motion.div>
  );
};
